import { toRelativeUrl } from '@okta/okta-auth-js';
import { Security, useOktaAuth } from '@okta/okta-react';
import type { Dispatch, ReactElement, ReactNode, SetStateAction} from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import oktaAuth from './oktaAuth';
import type { RestoreOriginalUriFunction } from '@okta/okta-react/bundles/types/OktaContext';
import { useDispatch, useSelector } from 'react-redux';
import { AuthActions } from 'common/auth/actions';
import { CILogger } from 'common/log/log.provider';
import { featureFlags } from 'common/featureFlags/featureFlags';
import type { RootState } from 'store';
import { AuthUserType } from 'common/graphql/types';
import { includes, replace, throttle, toLower } from 'lodash';
import { isUserLoggedIn } from 'common/auth/auth_route';
import { CIPageStatus } from 'common/viewcomp/general/ci_pagestatus';
import { useTranslation } from 'react-i18next';
import { useClient } from 'common/hooks/use-client';
import { saveLastPagesOpenedAndLogout, TimeoutConfigCognito, TimeoutConfigOkta } from 'common/viewcomp/layout/helpers';
import { useFxIntegrationInitialization } from 'common/featureFlags/hooks/useFxIntegrationInitialization';
import { useOnVisibilityChange } from 'common/hooks/useOnVisibilityChange';
import { useRetry } from 'common/hooks/useRetry';

const isUserCurrentlyActive = (inactivityThresholdSec: number) => {
    const lastActivity = +(sessionStorage.getItem(TimeoutConfigCognito.storageKey) || 0);
    const now = Math.floor(Date.now() / 1000);

    return now - lastActivity < inactivityThresholdSec;
};

interface Props {
    children?: ReactNode;
}

const AuthStateMonitor = ({ children }: Props): ReactElement => {
    const { oktaAuth: oktaAuthInstance } = useOktaAuth();
    const auth = useSelector((state: RootState) => state.auth);
    const dispatch = useDispatch();
    const [sessionExpiration, setSessionExpiration] = useState<Date>(null);
    const inactivityThreshold = TimeoutConfigOkta.totalSessionDuration - TimeoutConfigCognito.dialogDuration; // matches the time when SessionTimeoutDialog shows the warning dialog

    const isOktaLogin =
        featureFlags.fxIntegration &&
        auth?.activeUser?.userType !== AuthUserType.PATIENT &&
        auth?.activeUser?.authenticationMethod === 'OKTA';

    const { checkOktaSession, checkIfOktaSessionExistsAndSignOutIfNot } = useCheckOktaSession(
        setSessionExpiration,
        isOktaLogin
    );

    useEffect(
        function verifyOktaSessionOnpageReload() {
            checkOktaSession();
        },
        [] // there is no dependency during signing out of user, it is meant only for page reload
    );

    useOnVisibilityChange(checkOktaSession);

    useEffect(
        function refreshSession() {
            if (!isOktaLogin || !isUserLoggedIn(auth) || !sessionExpiration) {
                return;
            }

            const sessionTimeoutMs = sessionExpiration.getTime() - Date.now();
            const keepAliveInterval = sessionTimeoutMs / 2;

            const intervalId = setInterval(async () => {
                const userIsActive = isUserCurrentlyActive(inactivityThreshold);
                if (userIsActive && document.visibilityState === 'visible') {
                    checkIfOktaSessionExistsAndSignOutIfNot();
                }
            }, keepAliveInterval);

            return () => clearInterval(intervalId);
        },
        [isOktaLogin, auth, checkIfOktaSessionExistsAndSignOutIfNot, sessionExpiration, inactivityThreshold]
    );

    useEffect(
        function initTokenManagerHandlers() {
            const handleTokenError = (error: any) => {
                CILogger.getInstance().error('Token renewal failed:', error);
                checkIfOktaSessionExistsAndSignOutIfNot();
            };

            const handleTokenRenewed = (key: string) => {
                CILogger.getInstance().info(`Token renewed: ${key}`);
            };

            oktaAuthInstance.tokenManager.on('error', handleTokenError);
            oktaAuthInstance.tokenManager.on('renewed', handleTokenRenewed);

            return () => {
                oktaAuthInstance.tokenManager.off('error', handleTokenError);
                oktaAuthInstance.tokenManager.off('renewed', handleTokenRenewed);
            };
        },
        [oktaAuthInstance, dispatch, checkIfOktaSessionExistsAndSignOutIfNot]
    );

    return <>{children}</>;
};

const useCheckOktaSession = (setSessionExpiration: Dispatch<SetStateAction<Date>>, isOktaLogin: boolean) => {
    const { oktaAuth: oktaAuthInstance } = useOktaAuth();
    const auth = useSelector((state: RootState) => state.auth);
    const client = useClient();
    const { t } = useTranslation('auth');
    const dispatch = useDispatch();
    const retry = useRetry();
    const fxIntegrationState = useFxIntegrationInitialization();

    const handleLogout = useCallback(() => {
        oktaAuthInstance.tokenManager.clear();
        dispatch(AuthActions.logoutRequest());
    }, [oktaAuthInstance, dispatch]);

    const checkIfOktaSessionExistsAndSignOutIfNot = useCallback(async () => {
        if (includes(window.location.pathname, '/sso/okta') || auth.switchingUser) {
            return;
        }

        // retry logic due to implementation of session.get() https://github.com/okta/okta-auth-js/blob/master/lib/session/api.ts as it returns inactive status even on network errors,
        // we might need to implement custom api call to okta to ignore on error
        const getSessionInfoWithRetries = async (): Promise<{ status: string; expiresAt: string; login: string }> => {
            let attemptCount = 0;
            const maxAttempts = 3;

            return await retry(
                async () => {
                    attemptCount++;
                    const sessionInfo = (await oktaAuthInstance.session.get()) as unknown as {
                        login: string;
                        status: string;
                        expiresAt: string;
                    };

                    if (attemptCount >= maxAttempts) {
                        return sessionInfo;
                    }

                    if (sessionInfo?.status === 'INACTIVE') {
                        CILogger.getInstance().warn(
                            `getSessionInfoWithRetries attempt ${attemptCount}/${maxAttempts} returned INACTIVE, retrying...`
                        );
                        throw new Error(`Session is INACTIVE (attempt ${attemptCount}/${maxAttempts})`);
                    }

                    return sessionInfo;
                },
                maxAttempts,
                2000 // 2 second delay between retries
            );
        };

        const sessionInfo = await getSessionInfoWithRetries();

        if (sessionInfo?.expiresAt) {
            setSessionExpiration(new Date(sessionInfo.expiresAt));
        }

        // double check the visibilityState as when leaving the page oktaSessiontExists may not be reliable
        if (document.visibilityState === 'hidden') {
            return;
        }

        if (!sessionInfo || sessionInfo.status !== 'ACTIVE') {
            CIPageStatus.error(t('login.sso.error.session_terminated'));
            CILogger.getInstance().info('User signed out because invalid okta session detected.');

            saveLastPagesOpenedAndLogout(auth, client, handleLogout);
        } else {
            const oktaLogin = sessionInfo?.login;
            const activeUserName = replace(auth?.activeUser?.name || '', /^FX_/, '');
            const isSameUser = toLower(oktaLogin) === toLower(activeUserName);

            // different user detected in okta, logout and sign in with new user
            if (!isSameUser && auth?.activeUser?.name) {
                CIPageStatus.error(t('login.sso.error.session_changed'));
                CILogger.getInstance().info(
                    `Different user detected in okta, logging out: ' + ${activeUserName} and signing in user: ' + ${oktaLogin}`
                );

                saveLastPagesOpenedAndLogout(auth, client, handleLogout);
                await oktaAuthInstance.signInWithRedirect();
            }
        }
    }, [auth, client, oktaAuthInstance, dispatch, handleLogout]);

    const throttledCheckIfOktaSessionExistsAndSignOutIfNot = useMemo(
        () => throttle(checkIfOktaSessionExistsAndSignOutIfNot, 10000),
        [checkIfOktaSessionExistsAndSignOutIfNot]
    );

    const checkIfOktaSessionExistsAndSignInIfYes = useCallback(async () => {
        if (isUserLoggedIn(auth) || includes(window.location.pathname, '/sso/okta') || auth.switchingUser) {
            return;
        }

        try {
            dispatch(AuthActions.setSSOLoading(true));

            const [oktaSessionExists, fxIntegrationEnabled] = await Promise.all([
                await oktaAuthInstance.session.exists(),
                await fxIntegrationState.waitForInitialization(),
            ]);

            if (oktaSessionExists && fxIntegrationEnabled) {
                await oktaAuthInstance.signInWithRedirect();
            } else {
                dispatch(AuthActions.setSSOLoading(false));
            }
        } catch (e) {
            dispatch(AuthActions.setSSOLoading(false));
            CILogger.getInstance().info('No SSO session detected', e);
        }
    }, [dispatch, oktaAuthInstance, auth, fxIntegrationState]);

    const checkOktaSession = useCallback(() => {
        if (isOktaLogin) {
            throttledCheckIfOktaSessionExistsAndSignOutIfNot();
        } else {
            checkIfOktaSessionExistsAndSignInIfYes();
        }
    }, [isOktaLogin, checkIfOktaSessionExistsAndSignInIfYes, throttledCheckIfOktaSessionExistsAndSignOutIfNot]);

    return { checkOktaSession, checkIfOktaSessionExistsAndSignOutIfNot };
};

const OktaAuthProvider = ({ children }: Props): ReactElement => {
    const history = useHistory();

    const restoreOriginalUri: RestoreOriginalUriFunction = (_oktaAuth, originalUri) => {
        history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
    };

    return (
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
            <AuthStateMonitor>{children}</AuthStateMonitor>
        </Security>
    );
};

export default OktaAuthProvider;
